import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  selectCellBoardShift,
  selectManufacturingLocation,
  useUserSettings,
} from '../../profile/utils';
import { EMPTY_GUID } from '../../../utils/string-utils';
import { QueryKeys } from '../../../api/queryKeys';
import { isBreakDisplaySuppressedPath } from '../utils';
import BreakTimeOverlay from './BreakTimeOverlay';

/**
 * Mounts the break-time announcement once for the whole app, against the cell + shift the user
 * has selected on the Cell Board. That selection is app-wide, so every page shows the
 * announcement for the same cell — except on the routes listed in `isBreakDisplaySuppressedPath`.
 *
 * Cell and shift are read straight from the settings query rather than through `usePersistedState`: this
 * component stays mounted for the whole session, and `usePersistedState` snapshots its value on
 * first render, so it would never see the user change either one.
 */
const BreakTimeOverlayHost = () => {
  const { mCellId } = useUserSettings(selectManufacturingLocation);
  const shift = useUserSettings(selectCellBoardShift);
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const isFirstRender = useRef(true);

  // Re-check on every navigation. The announcement query would otherwise only refetch when its
  // component mounts, and this host never unmounts, so a change an administrator makes to the
  // announcement configuration would not reach an open session until the browser was reloaded.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void queryClient.invalidateQueries({ queryKey: [QueryKeys.BREAK_DISPLAY] });
  }, [pathname, queryClient]);

  if (isBreakDisplaySuppressedPath(pathname)) {
    return null;
  }

  return <BreakTimeOverlay mCellId={mCellId ?? EMPTY_GUID} shift={shift} />;
};

export default BreakTimeOverlayHost;
